import { createContext, useContext, useState, useEffect, ReactNode } from "react"

interface UserProfile {
  id: string
  name: string
  email: string
  tier: 'initiate' | 'creator' | 'architect'
  points: number
  joinDate: string
  avatar: string
  preferences: {
    movement: string[]
    style: string[]
    notifications: Record<string, boolean>
  }
  stats: {
    totalPurchases: number
    movementSessions: number
    challengesCompleted: number
    communityPosts: number
  }
}

interface ApexState {
  // User
  user: UserProfile | null
  isOnboarded: boolean
  
  // Navigation
  activeTab: string
  
  // UI State
  showNotifications: boolean
  showProductModal: boolean
  selectedProduct: any
  
  // Data
  wishlistedItems: string[]
  cartItems: any[]
  achievements: any[]
  notifications: any[]
}

interface ApexContextType {
  state: ApexState
  actions: {
    setUser: (user: UserProfile) => void
    completeOnboarding: (userData: any) => void
    setActiveTab: (tab: string) => void
    toggleNotifications: () => void
    showProductDetails: (product: any) => void
    hideProductModal: () => void
    addToWishlist: (productId: string) => void
    removeFromWishlist: (productId: string) => void
    addToCart: (item: any) => void
    markNotificationAsRead: (id: string) => void
    unlockAchievement: (achievement: any) => void
  }
}

const ApexContext = createContext<ApexContextType | null>(null)

const mockUser: UserProfile = {
  id: "1",
  name: "Alex Chen",
  email: "alex.chen@example.com",
  tier: "creator",
  points: 2840,
  joinDate: "March 2024",
  avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
  preferences: {
    movement: ["urban", "studio"],
    style: ["architect", "kinetic"],
    notifications: {
      drops: true,
      challenges: true,
      events: false,
      community: true
    }
  },
  stats: {
    totalPurchases: 12,
    movementSessions: 45,
    challengesCompleted: 8,
    communityPosts: 23
  }
}

export function ApexProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ApexState>({
    user: mockUser,
    isOnboarded: true, // Set to false for fresh onboarding
    activeTab: 'home',
    showNotifications: false,
    showProductModal: false,
    selectedProduct: null,
    wishlistedItems: ['2', '4'],
    cartItems: [],
    achievements: [],
    notifications: [
      {
        id: '1',
        type: 'drop',
        title: 'Architect Collection Drops Soon',
        message: 'Get ready! The exclusive Architect Collection launches in 2 hours.',
        timestamp: '5 minutes ago',
        isRead: false,
        priority: 'high'
      }
    ]
  })

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('apex-state')
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState)
        setState(prev => ({ ...prev, ...parsed }))
      } catch (error) {
        console.error('Error loading saved state:', error)
      }
    }
  }, [])

  // Save state to localStorage on changes
  useEffect(() => {
    localStorage.setItem('apex-state', JSON.stringify({
      user: state.user,
      isOnboarded: state.isOnboarded,
      wishlistedItems: state.wishlistedItems,
      cartItems: state.cartItems,
      achievements: state.achievements
    }))
  }, [state.user, state.isOnboarded, state.wishlistedItems, state.cartItems, state.achievements])

  const actions = {
    setUser: (user: UserProfile) => {
      setState(prev => ({ ...prev, user }))
    },

    completeOnboarding: (userData: any) => {
      const newUser: UserProfile = {
        id: Date.now().toString(),
        name: userData.name,
        email: userData.email,
        tier: 'initiate',
        points: 0,
        joinDate: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        avatar: `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop`,
        preferences: {
          movement: userData.movement || [],
          style: userData.style || [],
          notifications: {
            drops: true,
            challenges: true,
            events: false,
            community: true
          }
        },
        stats: {
          totalPurchases: 0,
          movementSessions: 0,
          challengesCompleted: 0,
          communityPosts: 0
        }
      }
      
      setState(prev => ({
        ...prev,
        user: newUser,
        isOnboarded: true
      }))
    },

    setActiveTab: (tab: string) => {
      setState(prev => ({ ...prev, activeTab: tab }))
    },

    toggleNotifications: () => {
      setState(prev => ({ ...prev, showNotifications: !prev.showNotifications }))
    },

    showProductDetails: (product: any) => {
      setState(prev => ({ 
        ...prev, 
        showProductModal: true, 
        selectedProduct: product 
      }))
    },

    hideProductModal: () => {
      setState(prev => ({ 
        ...prev, 
        showProductModal: false, 
        selectedProduct: null 
      }))
    },

    addToWishlist: (productId: string) => {
      setState(prev => ({
        ...prev,
        wishlistedItems: prev.wishlistedItems.includes(productId) 
          ? prev.wishlistedItems
          : [...prev.wishlistedItems, productId]
      }))
    },

    removeFromWishlist: (productId: string) => {
      setState(prev => ({
        ...prev,
        wishlistedItems: prev.wishlistedItems.filter(id => id !== productId)
      }))
    },

    addToCart: (item: any) => {
      setState(prev => ({
        ...prev,
        cartItems: [...prev.cartItems, { ...item, id: Date.now() }]
      }))
    },

    markNotificationAsRead: (id: string) => {
      setState(prev => ({
        ...prev,
        notifications: prev.notifications.map(notif =>
          notif.id === id ? { ...notif, isRead: true } : notif
        )
      }))
    },

    unlockAchievement: (achievement: any) => {
      setState(prev => ({
        ...prev,
        achievements: [...prev.achievements, achievement],
        user: prev.user ? {
          ...prev.user,
          points: prev.user.points + achievement.points
        } : null
      }))
    }
  }

  return (
    <ApexContext.Provider value={{ state, actions }}>
      {children}
    </ApexContext.Provider>
  )
}

export function useApexState() {
  const context = useContext(ApexContext)
  if (!context) {
    throw new Error('useApexState must be used within ApexProvider')
  }
  return context
}